<template>
    <div class="modal fade a-modal" ref="modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content" v-if="user">
                <div class="modal-header">
                    <div class="d-flex align-items-center gap-3">
                        <div class="ud-avatar" :style="user.avatar_style && user.avatar_style.color ? { backgroundColor: user.avatar_style.color } : null">{{ (user.username || '?').charAt(0).toUpperCase() }}</div>
                        <div>
                            <h1 class="modal-title">{{ user.role == utils.USER_ROLES.GUEST ? 'Sin identificar' : user.username }}</h1>
                            <div class="d-flex gap-2 align-items-center mt-1">
                                <span class="a-pill" :class="online ? 'a-pill-ok' : 'a-pill-muted'">{{ online ? 'Conectado' : 'Desconectado' }}</span>
                                <span class="a-subtle" v-if="online && user.current_view">{{ view_label(user.current_view) }}</span>
                            </div>
                        </div>
                    </div>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                </div>
                <div class="modal-body">
                    <div class="ud-grid">
                        <div>
                            <div class="a-eyebrow mb-1">Puntos</div>
                            <div class="ud-big">{{ score }}</div>
                        </div>
                        <div>
                            <div class="a-eyebrow mb-1">Respuestas</div>
                            <div class="ud-big">{{ (user.data || []).length }}</div>
                        </div>
                        <div>
                            <div class="a-eyebrow mb-1">Rol</div>
                            <select v-if="is_admin" class="form-select form-select-sm" v-model="user.role" @change="send('CHANGE_ROLE', user)">
                                <option v-for="role in roles" :key="role" :value="role">{{ role_label(role) }}</option>
                            </select>
                            <div v-else>{{ role_label(user.role) }}</div>
                        </div>
                    </div>

                    <div v-if="is_admin && online" class="d-flex gap-2 mt-3">
                        <button type="button" class="btn btn-secondary btn-sm" @click="send('CLIENT_RELOAD', user.id)"><i class="bi bi-arrow-clockwise"></i>Recargar su pantalla</button>
                        <button type="button" class="btn btn-secondary btn-sm" @click="send('CLIENT_KICK', user.id); close()"><i class="bi bi-box-arrow-right"></i>Expulsar</button>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mt-4 mb-2">
                        <h2 class="a-section-title">Respuestas</h2>
                        <button v-if="is_admin && (user.data || []).length" type="button" class="btn btn-ghost btn-sm text-danger" @click="send('DELETE_USER_DATA', { user_id: user.id, question_id: null })"><i class="bi bi-trash3"></i>Borrar todas</button>
                    </div>
                    <div class="a-card">
                        <ul class="a-list" v-if="answers.length">
                            <li class="a-row" v-for="a in answers" :key="a.question_id">
                                <div class="a-row-main">
                                    <div class="a-row-title">{{ a.title }}</div>
                                    <div class="a-row-meta"><span>#{{ a.question_id }}</span><span v-if="a.time != null"><i class="bi bi-stopwatch"></i>{{ a.time }} s</span></div>
                                </div>
                                <span class="a-pill" v-if="a.result" :class="a.result.correct ? 'a-pill-ok' : ''">{{ a.result.correct ? 'Acierto' : (a.content ? 'Visto' : 'Fallo') }}</span>
                                <button v-if="is_admin" type="button" class="btn btn-ghost btn-icon btn-sm" title="Borrar esta respuesta" @click="send('DELETE_USER_DATA', { user_id: user.id, question_id: a.question_id })"><i class="bi bi-x-lg"></i></button>
                            </li>
                        </ul>
                        <div v-else class="a-empty py-4">Todavía no ha respondido nada.</div>
                    </div>

                    <details v-if="user.device" class="ud-device mt-3">
                        <summary>Dispositivo</summary>
                        <div class="a-row-meta mt-2">
                            <span>{{ user.device.client && user.device.client.name }} {{ user.device.client && user.device.client.version }}</span>
                            <span>{{ user.device.os && user.device.os.name }} {{ user.device.os && user.device.os.version }}</span>
                            <span v-if="user.ip">IP {{ user.ip }}</span>
                            <span>ID {{ user.id }}</span>
                        </div>
                    </details>
                </div>
                <div class="modal-footer justify-content-between">
                    <button v-if="is_admin && online" type="button" class="btn btn-ghost text-danger" @click="send('CLIENT_DESTROY', user.id); close()"><i class="bi bi-trash3"></i>Eliminar usuario</button>
                    <span v-else></span>
                    <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Cerrar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from 'bootstrap'
import utils from '@/../../shared/utils'
import { role_label, view_label } from '@/utils/admin-format'

// One player/staff member: who they are, how they're doing, their answers -
// and the moderation actions. Used from every list of people in the panel.
export default {
    name: 'UserDetailModal',
    data(){
        return { utils, uuid: null };
    },
    computed: {
        // Connected clients first (live data), else the registered roster.
        user(){
            if(!this.uuid) return null;
            const game = this.$store.state.game;
            return (game.clients || []).find((u) => u.uuid == this.uuid) || (game.users || []).find((u) => u.uuid == this.uuid) || null;
        },
        online(){
            return !!(this.$store.state.game.clients || []).find((u) => u.uuid == this.uuid);
        },
        is_admin(){
            const me = this.$store.state.game.user;
            return !!me && me.role == utils.USER_ROLES.ADMINISTRATOR;
        },
        roles(){
            return [utils.USER_ROLES.PLAYER, utils.USER_ROLES.MANAGER, utils.USER_ROLES.ASSISTANT, utils.USER_ROLES.ADMINISTRATOR, utils.USER_ROLES.GUEST];
        },
        score(){
            const s = utils.calc_score(this.user.data || []);
            return s ? Math.round(s) : 0;
        },
        answers(){
            return (this.user.data || []).map((d) => {
                const q = utils.find_question(d.question);
                return {
                    question_id: d.question,
                    title: q ? q.question.title : 'Actividad eliminada',
                    time: q ? utils.get_answer_time(q.question.start_time, d.timestamp) : null,
                    content: q ? utils.is_content_activity(q.question.type) : false,
                    result: q ? utils.calc_question_result(this.user.data, d.question) : null,
                };
            });
        }
    },
    mounted(){
        this.modal = new Modal(this.$refs.modal);
        this.$refs.modal.addEventListener('hidden.bs.modal', () => { this.uuid = null; });
    },
    beforeUnmount(){
        if(this.modal) this.modal.dispose();
    },
    methods: {
        role_label, view_label,
        open(uuid){
            this.uuid = uuid;
            this.modal.show();
        },
        close(){
            this.modal.hide();
        },
        send(rpc, params){
            window.network.wso.send(JSON.stringify({ action: window.RPC[rpc], params }));
        }
    }
}
</script>

<style scoped>
.ud-avatar{
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: var(--a-ink);
    color: #fff;
    font-weight: 800;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.ud-grid{
    display: grid;
    grid-template-columns: 1fr 1fr 1.4fr;
    gap: 16px;
    align-items: start;
}
.ud-big{
    font-family: "Archivo", sans-serif;
    font-weight: 800;
    font-size: 1.6rem;
    line-height: 1;
}
.ud-device summary{
    cursor: pointer;
    color: var(--a-text-2);
    font-size: 0.85rem;
}
</style>
